<script setup>
import { useRouter } from 'vue-router'
import { useDrawer } from '../composables/useDrawer.js'
import { useLocation } from '../composables/useLocation.js'
import { setLocale, locale, SUPPORTED_LOCALES } from '../i18n/index.js'
import OPERALogo from './icons/OPERALogo.vue'
import UiIcon from './icons/UiIcon.vue'

const { open, close } = useDrawer()
const { activeLocation, savedLocations, locating, setLocation, useDeviceLocation, removeLocation, displayLoc } = useLocation()
const router = useRouter()

const locales = SUPPORTED_LOCALES
const currentLocale = locale

/* 菜单列表：天气首页 / 每小时预报 / 天气预测 / 生活指数 / 隐私协议 / 使用条款 / 关于我们 */
const nav = [
  { to: '/', labelKey: 'nav.home', icon: 'home' },
  { to: '/hourly', labelKey: 'nav.hourly', icon: 'clock' },
  { to: '/monthly', labelKey: 'nav.monthly', icon: 'calendar' },
  { to: '/activity', labelKey: 'nav.activity', icon: 'activity' },
  { to: '/privacy', labelKey: 'nav.privacy', icon: 'shield' },
  { to: '/terms', labelKey: 'nav.terms', icon: 'doc' }
]

async function go(to) {
  close()
  await router.push(to)
}

async function pick(loc) {
  close()
  await setLocation(loc)
  await router.push('/')
}

async function locate() {
  close()
  const loc = await useDeviceLocation()
  if (loc) await router.push('/')
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="open" class="scrim" @click="close" />
    </Transition>
    <Transition name="slide">
      <aside v-if="open" class="drawer" role="dialog" :aria-label="$t('nav.dialog')">
        <header class="drawer__head">
          <OPERALogo :width="120" :height="22" />
          <button class="drawer__close" :aria-label="$t('nav.close')" @click="close">
            <UiIcon name="close" :size="22" />
          </button>
        </header>

        <nav class="drawer__nav">
          <button
            v-for="n in nav" :key="n.to"
            class="drawer__item"
            :class="{ 'drawer__item--on': $route.path === n.to }"
            @click="go(n.to)"
          >
            <UiIcon :name="n.icon" :size="20" />
            <span>{{ $t(n.labelKey) }}</span>
          </button>
        </nav>

        <div class="drawer__div" />

        <button class="drawer__locate" :disabled="locating" @click="locate">
          <UiIcon name="myLocation" :size="18" />
          <span>{{ locating ? $t('nav.locating') : $t('nav.useLocation') }}</span>
        </button>

        <section class="drawer__saved" v-if="savedLocations.length">
          <h3 class="drawer__sub">{{ $t('nav.saved') }}</h3>
          <div
            v-for="l in savedLocations" :key="l.id"
            class="drawer__sav" :class="{ 'drawer__item--on': l.id === activeLocation.id }"
          >
            <button class="drawer__pick" @click="pick(l)">
              <UiIcon name="pin" :size="18" />
              <span class="drawer__locname">{{ displayLoc(l).name }}</span>
            </button>
            <button
              v-if="l.id !== activeLocation.id"
              class="drawer__rm" :aria-label="$t('loc.remove')" @click="removeLocation(l.id)"
            >
              <UiIcon name="close" :size="16" />
            </button>
          </div>
        </section>

        <button class="drawer__manage" @click="go('/location')">{{ $t('nav.manage') }}</button>

        <!-- 语言切换（默认跟随浏览器系统语言，可手动覆盖并持久化） -->
        <!-- 隐藏语言切换，浏览器自动识别 -->
        <!-- <section class="drawer__langSec">
          <h3 class="drawer__sub">{{ $t('nav.language') }}</h3>
          <div class="drawer__langRow">
            <button
              v-for="l in locales" :key="l.code"
              class="drawer__langBtn" :class="{ 'drawer__langBtn--on': l.code === currentLocale }"
              :aria-pressed="l.code === currentLocale"
              @click="setLocale(l.code)"
            >{{ l.label }}</button>
          </div>
        </section> -->
      </aside>
    </Transition>
  </Teleport>
</template>

<style scoped>
.scrim { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 40; }
.drawer {
  position: fixed; top: 0; left: 0; bottom: 0; width: 82%; max-width: 340px;
  /* 抽屉是浮层，用 overlay 面板保证在浅色天气底上也清晰 */
  background: var(--overlay-bg);
  backdrop-filter: var(--surface-blur);
  -webkit-backdrop-filter: var(--surface-blur);
  border-right: 1px solid var(--divider);
  z-index: 50; display: flex; flex-direction: column;
  padding: var(--sp-4) var(--sp-3) calc(var(--sp-6) + var(--safe-bottom));
  box-shadow: var(--shadow-3); overflow-y: auto;
}
.drawer__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--sp-4); }
.drawer__close { width: 40px; height: 40px; display: grid; place-items: center; color: var(--text-secondary); border-radius: var(--r-pill); }
.drawer__close:hover { background: var(--overlay-hover); }

.drawer__nav { display: flex; flex-direction: column; gap: 2px; margin-bottom: var(--sp-3); }
.drawer__item {
  display: flex; align-items: center; gap: var(--sp-3); width: 100%;
  padding: 12px var(--sp-3); border-radius: var(--r-2); color: var(--text-primary);
  font-size: 15px; text-align: left;
}
.drawer__item:hover { background: var(--overlay-hover); }
.drawer__item--on { background: rgba(10,100,196,.10); color: var(--brand); font-weight: var(--fw-semibold); }
.drawer__item--sm { font-size: 14px; padding: 10px var(--sp-3); }
.drawer__div { height: 1px; background: var(--divider); margin: var(--sp-2) var(--sp-3) 0; }
.drawer__locname { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.drawer__locate {
  display: flex; align-items: center; justify-content: center; gap: var(--sp-2);
  width: 100%; padding: 12px; margin: var(--sp-2) 0 var(--sp-4);
  background: var(--brand-btn); color: #fff; border-radius: var(--r-2); font-size: 14px; font-weight: var(--fw-semibold);
}
.drawer__locate:disabled { opacity: .6; }

.drawer__saved { display: flex; flex-direction: column; gap: 2px; }
.drawer__sav { display: flex; align-items: center; border-radius: var(--r-2); }
.drawer__sav.drawer__item--on { background: rgba(10,100,196,.10); }
.drawer__pick { flex: 1; display: flex; align-items: center; gap: var(--sp-3); padding: 10px var(--sp-3); font-size: 14px; text-align: left; color: var(--text-primary); }
.drawer__rm { width: 32px; height: 32px; display: grid; place-items: center; color: var(--text-tertiary); border-radius: var(--r-pill); margin-right: var(--sp-2); }
.drawer__rm:hover { background: var(--overlay-hover); color: var(--color-rain); }
.drawer__sub { margin: 0 0 var(--sp-2); padding: 0 var(--sp-3); font-size: 12px; color: var(--text-tertiary); font-weight: var(--fw-semibold); }
.drawer__manage { margin-top: var(--sp-4); padding: 12px; text-align: center; color: var(--text-link); font-size: 14px; }

.drawer__langSec { margin-top: var(--sp-4); }
.drawer__langRow { display: flex; gap: var(--sp-2); padding: 0 var(--sp-3); }
.drawer__langBtn {
  flex: 1; padding: 9px 0; border-radius: var(--r-pill); font-size: 13px;
  border: 1px solid var(--divider); color: var(--text-secondary);
}
.drawer__langBtn--on { background: var(--brand); border-color: var(--brand); color: #fff; font-weight: var(--fw-semibold); }

.fade-enter-active, .fade-leave-active { transition: opacity var(--d-med) var(--ease-out); }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.slide-enter-active, .slide-leave-active { transition: transform var(--d-med) var(--ease-out); }
.slide-enter-from, .slide-leave-to { transform: translateX(-100%); }
</style>
